/* ==========================================================================
   Chrune - cart drawer
   Loaded after theme.js, which delegates the drawer entirely to this file.

   Four bugs were found in the browser and are fixed here:

   1. The drawer was refreshed with `/cart?sections=...` plus an
      `Accept: application/json` header. That combination hits the cart AJAX
      API, which answers with the cart object and no sections at all, so the
      drawer never received any markup. The correct call for a single section
      is `?section_id=`, which returns raw HTML.

   2. Opening the drawer was chained behind that fetch, so an empty or failed
      response left the panel shut with no feedback. Opening a panel must
      never depend on a network call.

   3. The overlay was revealed inside requestAnimationFrame. A frame is not
      guaranteed to run promptly when the page is not painting, which left the
      scrim invisible. A forced reflow makes the transition deterministic.

   4. The step of the plus and minus buttons is carried by data-line-qty, so
      it reads back as dataset.lineQty. The handler read dataset.qty, which is
      undefined: the quantity posted to Shopify was NaN and the line never
      moved. The same slip was in theme.js, for the cart page.

   A line is changed by its Shopify line item key, never by its position. Two
   lines of the same piece with different engravings share a variant, and a
   position read from markup that is out of date names whichever line now sits
   there. A key that no longer exists is refused by Shopify instead, and the
   page reloads on the real cart.
   ========================================================================== */

(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var overlay = document.querySelector('[data-overlay]');
  var lastFocus = null;
  var pending = window.chruneCartPending || { start: function () {}, end: function () {} };

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function showOverlay() {
    if (!overlay) return;
    overlay.hidden = false;
    void overlay.offsetWidth; // flush layout so the opacity transition runs
    overlay.classList.add('is-active');
  }

  function hideOverlay() {
    if (!overlay) return;
    overlay.classList.remove('is-active');
    window.setTimeout(function () { overlay.hidden = true; }, 220);
  }

  function openDrawer(trigger) {
    lastFocus = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    showOverlay();
    var first = drawer.querySelector('button, a[href], input');
    if (first) first.focus({ preventScroll: true });
  }

  function closeDrawer() {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    hideOverlay();
    if (lastFocus) lastFocus.focus({ preventScroll: true });
  }

  function paint(html) {
    var body = drawer.querySelector('[data-cart-drawer-body]');
    if (!body || !html) return;
    var content = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-cart-drawer-content]');
    if (content) body.innerHTML = content.outerHTML;
  }

  function refresh() {
    return fetch(root() + 'cart?section_id=cart-drawer-items')
      .then(function (r) { return r.text(); })
      .then(paint)
      .catch(function () { /* the drawer keeps the markup it already has */ });
  }

  function setCount(n) {
    var el = document.querySelector('[data-cart-count]');
    if (!el) return;
    el.textContent = n;
    el.hidden = n === 0;
    el.classList.add('is-bumped');
    window.setTimeout(function () { el.classList.remove('is-bumped'); }, 180);
  }

  function changeLine(key, quantity) {
    if (!key) return;
    drawer.classList.add('is-loading');
    var cartRoot = document.querySelector('[data-cart-root]');
    var sections = ['cart-drawer-items'];
    if (cartRoot && cartRoot.dataset.sectionId) sections.push(cartRoot.dataset.sectionId);
    if (cartRoot) cartRoot.classList.add('is-loading');
    pending.start();

    fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: String(key),
        quantity: quantity,
        sections: sections.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
      .then(function (cart) {
        setCount(cart.item_count);
        if (cart.sections && cart.sections['cart-drawer-items']) {
          paint(cart.sections['cart-drawer-items']);
        }
        if (cartRoot && cartRoot.dataset.sectionId && cart.sections && cart.sections[cartRoot.dataset.sectionId]) {
          var fresh = new DOMParser()
            .parseFromString(cart.sections[cartRoot.dataset.sectionId], 'text/html')
            .querySelector('[data-cart-root]');
          if (fresh) cartRoot.innerHTML = fresh.innerHTML;
        }
      })
      .catch(function () { window.location.reload(); })
      .finally(function () {
        pending.end();
        drawer.classList.remove('is-loading');
        if (cartRoot) cartRoot.classList.remove('is-loading');
      });
  }

  document.querySelectorAll('[data-cart-open]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer(trigger);
      refresh();
    });
  });

  var closeBtn = drawer.querySelector('[data-cart-close]');
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  if (overlay) overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  drawer.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-line-remove]');
    if (remove) { changeLine(remove.dataset.key, 0); return; }
    var step = e.target.closest('[data-line-qty]');
    if (step) {
      var line = step.closest('.line-item');
      var input = line && line.querySelector('[data-line-input]');
      if (!input) return;
      var stepBy = parseInt(step.dataset.lineQty, 10);
      if (isNaN(stepBy)) return;
      var val = parseInt(input.value, 10) || 1;
      changeLine(step.dataset.key, Math.max(0, val + stepBy));
    }
  });

  drawer.addEventListener('change', function (e) {
    var input = e.target.closest('[data-line-input]');
    if (!input) return;
    changeLine(input.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
  });

  drawer.addEventListener('submit', function (e) {
    if (!e.target.matches('[data-cart-form]')) return;
    var submitter = e.submitter;
    if (submitter && submitter.name === 'checkout') return; // let checkout post normally
    e.preventDefault();
  });

  // The product form opens the drawer after a successful add.
  document.addEventListener('chrune:cart-added', function () {
    openDrawer(null);
    refresh();
  });
})();